import { ApexOptions } from 'apexcharts';
import { Theme } from '@mui/material/styles';

export interface ChartConfigOptions {
  theme: Theme;
  chartType?: 'bar' | 'line' | 'area';
  height?: number;
  showToolbar?: boolean;
  showLegend?: boolean;
  showDataLabels?: boolean;
  strokeWidth?: number | number[];
  fillOpacity?: number;
  stacked?: boolean;
  foreColor?: string;
  chartId?: string;
  isMobile?: boolean;
}

export interface DateTimeChartConfigOptions extends ChartConfigOptions {
  minTimestamp?: number;
  maxTimestamp?: number;
  nowTime?: Date;
  showNowMarker?: boolean;
  tickAmount?: number;
}

export interface CategoryChartConfigOptions extends ChartConfigOptions {
  categories?: any[];
  tickAmount?: number;
  tickPlacement?: 'between' | 'on';
  labelFormatter?: (value: string) => string;
}

/**
 * Creates base chart configuration with common styling
 */
export const createBaseChartConfig = (options: ChartConfigOptions): Partial<ApexOptions> => {
  const {
    theme,
    chartType = 'bar',
    height = 350,
    showToolbar = false,
    showLegend = false,
    showDataLabels = false,
    strokeWidth = 0,
    fillOpacity = 1,
    stacked = false,
    foreColor,
    chartId,
    isMobile = false
  } = options;

  return {
    chart: {
      type: chartType,
      height,
      background: 'transparent',
      foreColor: foreColor || theme.palette.text.primary,
      toolbar: {
        show: showToolbar,
        tools: {
          download: false
        }
      },
      animations: {
        enabled: true,
        easing: 'easeinout',
        speed: 800
      },
      ...(chartId && { id: chartId }),
      ...(stacked && { stacked })
    },
    plotOptions: {
      bar: {
        borderRadius: 2,
        columnWidth: '80%'
      }
    },
    stroke: {
      width: strokeWidth
    },
    dataLabels: {
      enabled: showDataLabels
    },
    fill: {
      opacity: fillOpacity
    },
    legend: {
      show: showLegend,
      fontSize: isMobile ? '13px' : '16px',
      fontFamily: 'Inter, sans-serif',
      labels: {
        colors: theme.palette.text.primary
      }
    },
    grid: {
      strokeDashArray: 3,
      borderColor: theme.palette.divider,
      xaxis: {
        lines: {
          show: false
        }
      },
        yaxis: {
          lines: {
            show: true
          }
        }
    },
    tooltip: {
      theme: theme.palette.mode,
      style: {
        fontSize: '12px',
        fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif'
      }
    }
  };
};

/**
 * Creates datetime-based chart configuration (for ElectricityRatesChart and CarbonIntensityChart)
 */
export const createDateTimeChartConfig = (options: DateTimeChartConfigOptions): Partial<ApexOptions> => {
  const {
    theme,
    minTimestamp,
    maxTimestamp,
    nowTime,
    showNowMarker = false,
    tickAmount = 8,
    ...baseOptions
  } = options;

  const baseConfig = createBaseChartConfig({ ...baseOptions, theme });

  const datetimeConfig: Partial<ApexOptions> = {
    ...baseConfig,
    xaxis: {
      type: 'datetime',
      ...(minTimestamp && { min: minTimestamp }),
      ...(maxTimestamp && { max: maxTimestamp }),
      labels: {
        datetimeFormatter: {
          hour: 'HH:mm'
        },
        style: {
          fontSize: baseOptions.isMobile ? '13px' : '18px',
          colors: theme.palette.text.secondary,
          fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif'
        },
        datetimeUTC: false
      },
      axisBorder: {
        show: false
      },
      axisTicks: {
        show: true,
        color: theme.palette.divider,
        height: 6
      },
      tickAmount
    }
  };

  // Add "Now" marker annotation if requested
  if (showNowMarker && nowTime) {
    datetimeConfig.annotations = {
      xaxis: [
        {
          x: nowTime.getTime(),
          borderColor: '#ffffff',
          borderWidth: 2,
          strokeDashArray: 5,
          label: {
            text: 'Now',
            style: {
              color: '#000000',
              fontSize: '12px',
              fontWeight: 'bold',
              fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif'
            },
            orientation: 'vertical',
            offsetY: -10
          }
        }
      ]
    };
  }

  return datetimeConfig;
};

/**
 * Creates category-based chart configuration (for ConsumptionChart and EmissionChart)
 */
export const createCategoryChartConfig = (options: CategoryChartConfigOptions): Partial<ApexOptions> => {
  const {
    theme,
    categories,
    tickAmount = 6,
    tickPlacement = 'between',
    labelFormatter,
    ...baseOptions
  } = options;

  const baseConfig = createBaseChartConfig({ ...baseOptions, theme });

  return {
    ...baseConfig,
    xaxis: {
      categories,
      tickPlacement,
      tickAmount,
      labels: {
        rotate: 0,
        style: {
          fontSize: baseOptions.isMobile ? '13px' : '16px',
          fontFamily: 'Inter, sans-serif',
          fontWeight: 500,
          colors: theme.palette.text.primary
        },
        ...(labelFormatter && { formatter: labelFormatter })
      },
      axisBorder: {
        color: theme.palette.divider
      },
      axisTicks: {
        color: theme.palette.divider
      }
    }
  };
};

/**
 * Creates common y-axis configuration
 */
export const createYAxisConfig = (
  theme: Theme,
  title: string,
  options: {
    min?: number;
    max?: number;
    tickAmount?: number;
    decimalsInFloat?: number;
    opposite?: boolean;
    id?: string;
    isMobile?: boolean;
  } = {}
): any => {
  const {
    min,
    max,
    tickAmount,
    decimalsInFloat,
    opposite = false,
    id,
    isMobile = false
  } = options;

  return {
    ...(id && { id }),
    ...(opposite && { opposite }),
    ...(min !== undefined && { min }),
    ...(max !== undefined && { max }),
    ...(tickAmount && { tickAmount }),
    ...(decimalsInFloat !== undefined && { decimalsInFloat }),
    title: {
      text: title,
      style: {
        fontSize: isMobile ? '13px' : '16px',
        fontFamily: 'Inter, sans-serif',
        fontWeight: isMobile ? 500 : 700,
        color: theme.palette.text.primary
      }
    },
    labels: {
      style: {
        fontSize: isMobile ? '12px' : '16px',
        fontFamily: 'Inter, sans-serif',
        fontWeight: 500,
        colors: theme.palette.text.primary
      }
    },
    axisBorder: {
      color: theme.palette.divider
    },
    axisTicks: {
      color: theme.palette.divider
    }
  };
};

/**
 * Common color palettes for different chart types
 */
export const ChartColors = {
  electricityRates: ['#6666ff', '#00cc00', '#ffeb00', '#ffa500', '#ff0000', '#800080'],
  carbonIntensity: ['#00cc00', '#ffeb00', '#ffa500', '#ff0000', '#800080'],
  consumption: {
    dark: {
      consumption: '#4caf50',
      price: '#720098ff',
      cost: '#1976d2',
      grid: '#424242',
      text: '#ffffff'
    },
    light: {
      consumption: '#00aa00',
      price: '#9c27b0',
      cost: '#1976d2',
      grid: '#777777',
      text: '#000000'
    }
  },
  emissions: {
    dark: {
      emissions: '#67330eff',
      gas: '#b69424ff',
      intensity: '#1976d2',
      grid: '#777777',
      text: '#ffffff'
    },
    light: {
      emissions: '#714627ff',
      gas: '#b79833ff',
      intensity: '#1976d2',
      grid: '#777777',
      text: '#000000'
    }
  },
  histogram: {
    dark: {
      first25: '#d32f2f',    // Red for highest emitters
      first50: '#f57c00',    // Orange for high emitters
      first75: '#fbc02d',    // Yellow for medium emitters
      remaining: '#388e3c',  // Green for lowest emitters
      grid: '#777777',
      text: '#ffffff'
    },
    light: {
      first25: '#d32f2f',    // Red for highest emitters
      first50: '#f57c00',    // Orange for high emitters
      first75: '#fbc02d',    // Yellow for medium emitters
      remaining: '#388e3c',  // Green for lowest emitters
      grid: '#777777',
      text: '#000000'
    }
  }
};

/**
 * Helper function to get theme-aware colors for consumption charts
 */
export const getConsumptionColors = (theme: Theme) => {
  const colors = ChartColors.consumption;
  return theme.palette.mode === 'dark' ? colors.dark : colors.light;
};

/**
 * Helper function to get theme-aware colors for emissions charts
 */
export const getEmissionsColors = (theme: Theme) => {
  const colors = ChartColors.emissions;
  return theme.palette.mode === 'dark' ? colors.dark : colors.light;
};

/**
 * Helper function to get theme-aware colors for histogram charts
 */
export const getHistogramColors = (theme: Theme) => {
  const colors = ChartColors.histogram;
  return theme.palette.mode === 'dark' ? colors.dark : colors.light;
};
